import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Diamond } from "@src/types/DiamondDetail";
import { FlatList, Linking, Platform, Pressable } from "react-native";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { Image as ExpoImage } from "expo-image";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import LabelView from "@components/molecules/LabelView";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import currency from "currency.js";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { getPDPHeaderCustomisation, hasZeroDecimals } from "./utils";
import { css } from "styled-components/native";
import LoaderModel from "@components/molecules/LoaderModel/LoaderModel";
import CollectionModal from "@components/molecules/Collections/CollectionModal";
import FavoritesModel from "@components/molecules/GalleryView/utils/FavoritesModel";
import { useEffect, useState } from "react";
import { ProductCategory } from "@utils/constants";
import {
  useAddCollection,
  useCollectionList,
  useProductAddFavorites,
  useProductDeleteFavorites,
} from "@apis/collection";
import { useQueryClient } from "@tanstack/react-query";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "@scenes/favorites/utils/collectionSchema";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { getToastBottomMargin } from "@scenes/searchResults/components/utils";
import { FavoritesEnum } from "@scenes/favorites/utils/FavoritesUtils";
import Routes from "@navigations/Routes";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { push } from "@navigations/utils/RootNavigation";
import {
  replaceDeeplinkScheme,
  isMeleeItem,
} from "@utils/product/productValidators";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { BuilderAlertManager } from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { useSelector } from "react-redux";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { useAppState } from "@redux/slices/appStateSlice";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { CustomisationKey } from "../types";

type MatchingBandProps = {
  matchingBandData: Diamond[];
  productType: ProductCategory;
  priceMode: number;
};

const MatchingBand = ({
  matchingBandData,
  productType,
  priceMode,
}: MatchingBandProps) => {
  const { typography, theme, user, t } = useScreenOptionsHooks();
  const { getPriceMode, disableImageZoom } = useUserInfo();
  const userCurrency = useUserCurrency();
  const [showLoaderModal, setLoaderModal] = useState(false);
  const productPrefix = useProductPrefix();
  let enumProductType = ProductCategory.JEWELRY as ProductCategory;
  const showMatchingBandSection = useGetPdpSectionLayout(
    PdpSections.MATCHING_BAND_DETAILS,
    productType
  );
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const { isCustomApp } = useAppState();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const [showCollectionListModal, setShowCollectionListModal] = useState(false);

  const addToFavorites = useProductAddFavorites(enumProductType);
  const removeFromFavorites = useProductDeleteFavorites();
  const queryClient = useQueryClient();
  const [itemIdArray, setItemIdArray] = useState<number[]>([]);
  const [mItem, setmItem] = useState<Diamond | undefined>(undefined);

  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType,
    SearchFilterTypeEnum.SINGLE_STONES
  );

  const objectStockNumber = getPDPHeaderCustomisation(
    theme,
    priceMode,
    sectionLayout,
    CustomisationKey.stock_no
  );

  const [enableCollectionButton, setEnableCollectionButton] = useState(false);
  const [isCollectionEventLoading, setCollectionEventLoading] = useState(false);

  const { bottom } = useSafeAreaInsets();

  const addCollection = useAddCollection();
  const { jcSelectedOption, personalization } = useJewelryConfiguratorState();

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const favoriteItemIdData = () => {
    queryClient.refetchQueries({ queryKey: ["SEARCH_RESULTS"] }).then();
  };

  const [isCreateNewCollectionModalOpen, setIsCreateNewCollectionModalOpen] =
    useState(false);
  const [collectionCount, setCollectionCount] = useState(0);

  const collectionListCall = useCollectionList(1, 99, "mix_sort");
  useEffect(() => {
    const collections = collectionListCall.data?.data.collections;
    if (collections) {
      setCollectionCount(collections.length);
    } else {
      setCollectionCount(0);
    }
  }, [collectionListCall.data?.data.collections]);

  const addToFavoritesData = (isFavorite: boolean, itemDetail: Diamond) => {
    setLoaderModal(true);
    if (isFavorite === undefined) {
      isFavorite = false;
    }

    let _payload = {
      type: "mix",
      ...(productType === ProductCategory.JEWELRY && {
        stock_item_id: itemDetail?.id ?? 0,
      }),
    };

    if (productType === ProductCategory.JEWELRY) {
      _payload = {
        ..._payload,
        ...(Object.keys(jcSelectedOption.current.option_value).length > 0 && {
          config: jcSelectedOption.current.option_value,
        }),
      };
      if (personalization) {
        _payload = {
          ..._payload,
          ...(Object.keys(personalization).length > 0 && {
            personalization: personalization ?? {},
          }),
        };
      }
    }
    if (!isFavorite) {
      addToFavorites.mutate(
        {
          favorite: _payload,
        },
        {
          onSuccess(data) {
            const diamondTempFavArray = new Array<number>(0);
            diamondTempFavArray.push(data.data.favorite.diamond_id);
            itemDetail.is_favorite = true;
            showToastMessage(itemDetail, true);
            setLoaderModal(false);
            favoriteItemIdData();
          },
          onSettled() {
            setLoaderModal(false);
          },
        }
      );
    } else {
      removeFromFavorites.mutate(
        {
          id: itemDetail?.id ?? 0,
        },
        {
          onSuccess: (data: any) => {
            const diamondTempFavArray = new Array<number>(0);
            diamondTempFavArray.push(data.data.favorite.diamond_id);
            itemDetail.is_favorite = false;
            showToastMessage(itemDetail, false);
            setLoaderModal(false);
            favoriteItemIdData();
          },
        }
      );
    }
  };

  const showToastMessage = (item?: Diamond, isFavorite?: boolean) => {
    showToast(
      isFavorite
        ? `${item?.short_title} ${t("searchResult.addToFavorites.text")}`
        : `${item?.short_title} ${t("searchResult.removeFromFavorites.text")}`,

      isFavorite ? (
        <Pressable
          onPress={() =>
            showCollectionModal(item?.id as number, item as Diamond)
          }
        >
          <VDBText color={theme["link-static"]}>
            {t("searchResult.tapToChangeCollection.text")}
          </VDBText>
        </Pressable>
      ) : (
        ""
      ),
      ToastTypes.SUCCESS,
      true,
      true,
      getToastBottomMargin(0, bottom)
    );
  };

  const showCollectionModal = (id: number, item: Diamond) => {
    setmItem(item);
    setItemIdArray([id]);
    if (collectionCount > 0) {
      setShowCollectionListModal(true);
    } else {
      setIsCreateNewCollectionModalOpen(true);
    }
  };

  const checkEnableCollectionButton = (etValue: string) => {
    setEnableCollectionButton(!!etValue);
  };

  const onSubmit = (mData: any) => {
    const alteredCollectionName = mData.collectionName.trim();

    addCollection.mutate(
      {
        name: alteredCollectionName,
        type: 5,
      },
      {
        onSuccess() {
          setCollectionEventLoading(false);
          setIsCreateNewCollectionModalOpen(false);
          setValue(FavoritesEnum.collection_name, "");
          checkEnableCollectionButton("");
          setTimeout(() => {
            showToast(
              t("global.text.done"),
              t("favorites.newCollectionCreated.text"),
              ToastTypes.SUCCESS,
              true,
              true
            );
          }, 1000);
          collectionListCall.refetch().then();
        },
      }
    );
  };

  const favoriteRemoved = (itemId: Array<number>) => {
    if (
      itemId &&
      itemId.length > 0 &&
      matchingBandData?.find((item) => item.id === itemId[0])
    ) {
      const item = matchingBandData?.find((i: Diamond) => i.id === itemId[0]);
      if (item) {
        item.is_favorite = false;
      }
      favoriteItemIdData();
    }
  };

  const handleItemClick = (item: Diamond) => {
    if (Platform.OS === "web") {
      if (item?.deeplink) {
        const deepLink = replaceDeeplinkScheme(
          item?.deeplink,
          process.env.EXPO_PUBLIC_APP_SCHEME
        );
        Linking.openURL(deepLink);
      }
    } else {
      const callback = () => {
        push(Routes.PDPScreen, {
          productID: item.id,
          productType: item.type,
          productPrefix,
        });
      };

      if (enableJewelryBuilder && history.length > 2) {
        BuilderAlertManager.open(callback);
      } else {
        callback();
      }
    }
  };

  const renderMatchingBandItem = ({
    item,
    index,
  }: {
    item: Diamond;
    index: number;
  }) => {
    return (
      <MatchingBandContainer applyMaxWidth={false} key={index}>
        <ItemContainer onPress={() => handleItemClick(item)}>
          <ExpoImage
            source={getImageSource(
              getOptimizedImageUrl(
                item.image_url ?? "",
                ImageSizePreset.LIST_THUMB
              ),
              ImageSizePreset.LIST_THUMB
            )}
            {...getIOSImageProps()}
            style={{
              width: 80,
              height: 80,
              borderRadius: SpaceType.Basic,
              borderWidth: 1,
              borderColor: theme["core-03"],
            }}
            contentFit={disableImageZoom() ? "contain" : "cover"}
          />
          <TextContainer>
            <LabelView
              itemLeftText={t(objectStockNumber[0]?.label, "Stock#")}
              itemRightText={item?.stock_num ?? "-"}
              keyTypographyStyle={typography["Body-R"]}
              valueTypographyStyle={typography["Body-M"]}
              itemLeftTextTheme={theme["core-06"]}
              rightPrefixTextTheme={theme["core-05"]}
              {...(theme.isMobile && { valueWidthPercent: 80 })}
            />
            <VDBText
              type={typography["H2-M"]}
              color={theme["core-05"]}
              numberOfLines={2}
            >
              {item?.short_title}
            </VDBText>
          </TextContainer>
        </ItemContainer>
        <FavoriteContainer
          theme={theme}
          isFavorite={item?.is_favorite}
          testID="favorite-icon"
          accessibilityLabel="Favorite button"
          onPress={() => {
            addToFavoritesData(item?.is_favorite ?? false, item);
          }}
        >
          <VDBIcon
            color={theme[item?.is_favorite ? "error-01" : "cont-00"]}
            size={20}
            variant={IconsVariant.VDBCustom}
            name={item?.is_favorite ? VDBIcons.FAV : VDBIcons.FAV_EMPTY}
          />
        </FavoriteContainer>
        {getPriceMode() !== PriceModes.GREEN && (
          <PriceContainer>
            <LabelView
              itemLeftText={
                isCustomApp && productType === ProductCategory.JEWELRY
                  ? item.is_finished_product === false
                    ? t("productDetailPage.forSettingOnly.text")
                    : t("productDetailPage.totalPrice.jewelry.text")
                  : t("productDetailPage.totalPrice.text")
              }
              itemRightText={
                item?.total_sales_price
                  ? currency(item?.total_sales_price, {
                      symbol: userCurrency,
                      precision:
                        (user?.feature_settings?.round_up_prices &&
                          !isMeleeItem(item)) ||
                        hasZeroDecimals(item?.total_sales_price)
                          ? 0
                          : 2,
                    }).format()
                  : "-"
              }
              keyTypographyStyle={typography["Body-R"]}
              valueTypographyStyle={typography["H1-B"]}
              itemLeftTextTheme={theme["core-06"]}
              rightPrefixTextTheme={theme["core-05"]}
            />
          </PriceContainer>
        )}
      </MatchingBandContainer>
    );
  };

  return (
    <Container>
      <TitleContainer>
        <VDBText type={typography["Large-B"]} color={theme["core-05"]}>
          {t(showMatchingBandSection?.label, "Matching Band")}
        </VDBText>
        <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
          {t(
            showMatchingBandSection?.description ?? "",
            "Find a wedding band that matches your engagement ring"
          )}
        </VDBText>
      </TitleContainer>
      <FlatList
        data={matchingBandData}
        renderItem={renderMatchingBandItem}
        horizontal={false}
        // contentContainerStyle={{ gap: SpaceType.Double }}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
      />

      <LoaderModel
        isOpen={showLoaderModal}
        onClose={() => setLoaderModal(false)}
      />

      <CollectionModal
        isOpen={showCollectionListModal}
        onClose={() => setShowCollectionListModal(false)}
        itemIdArray={itemIdArray}
        favItemIdArray={itemIdArray ?? []}
        item={mItem}
        collectionItemIdArray={[]}
        favoriteRemoved={favoriteRemoved}
        enableToastMessage={true}
      />

      <FavoritesModel
        control={control}
        setValue={setValue}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isCollectionEventLoading={isCollectionEventLoading}
        setIsCreateNewCollectionModalOpen={setIsCreateNewCollectionModalOpen}
        isCreateNewCollectionModalOpen={isCreateNewCollectionModalOpen}
        checkEnableCollectionButton={checkEnableCollectionButton}
        setCollectionEventLoading={setCollectionEventLoading}
        enableCollectionButton={enableCollectionButton}
      />
    </Container>
  );
};

export default MatchingBand;

const Container = styled.View`
  padding-top: ${SpaceType.Big}px;
`;

const TitleContainer = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
`;

const MatchingBandContainer = styled.View<{
  applyMaxWidth: boolean;
}>`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  margin-top: ${SpaceType.Double}px;
  ${({ applyMaxWidth }) =>
    applyMaxWidth &&
    css`
      width: 343px;
    `}
`;

const ItemContainer = styled.Pressable`
  padding: ${SpaceType.Double}px;
  gap: 12px;
  border-radius: ${SpaceType.Basic}px;
  flex-direction: row;
`;

const TextContainer = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
  align-items: flex-start;
  flex: 1;
`;

const FavoriteContainer = styled.Pressable<any>`
  height: 32px;
  width: 32px;
  border-top-right-radius: 6px;
  border-bottom-left-radius: 8px;
  background-color: ${({ theme, isFavorite }) =>
    theme[isFavorite ? "over-03" : "over-02"]};
  position: absolute;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  right: -0.5px;
  top: -0.5px;
  overflow: hidden;
`;

const PriceContainer = styled.View`
  border-top-width: 1px;
  border-color: ${({ theme }) => theme["core-03"]};
  padding: ${SpaceType.Double}px;
`;
